var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
var __async = (__this, __arguments, generator) => {
  return new Promise((resolve, reject) => {
    var fulfilled = (value) => {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    };
    var rejected = (value) => {
      try {
        step(generator.throw(value));
      } catch (e) {
        reject(e);
      }
    };
    var step = (x) => x.done ? resolve(x.value) : Promise.resolve(x.value).then(fulfilled, rejected);
    step((generator = generator.apply(__this, __arguments)).next());
  });
};
import { LogLevel, InteractionRequiredAuthError, AuthError, BrowserAuthError, BrowserConfigurationAuthError, PublicClientApplication } from "@azure/msal-browser";
import Bowser from "bowser";
import { v4 } from "uuid";
import logger, { ErrorType } from "../logging/Logger.js";
import { MsalNavigationClient } from "./MsalNavigationClient.js";
import { Constants, LAST_TENANT_ID, LAST_WORKSPACE_ID, LAST_EXPERIMENTATION_GROUP } from "./MSAL.constants.js";
import { AuthUtils } from "./AuthUtils.js";
import { TelemetryEventName } from "./TelemetryEventName.js";
import { ErrorCodes } from "./interface.js";
import { TelemetryExceptionName } from "./TelemetryExceptionName.js";
import { parseIdToken } from "./parseIdToken.js";
import LocalStorageService from "../LocalStorageService.js";
import EnvironmentService from "../EnvironmentService.js";
const _MsalAuthContext = class _MsalAuthContext {
  constructor(history, config) {
    __publicField(this, "loginRedirectUri", Constants.route.baseUrl);
    __publicField(this, "silentRedirectUri", Constants.route.silentRedirectUrl);
    __publicField(this, "postLogoutRedirectUri", Constants.route.onboardingPage);
    __publicField(this, "clientId", EnvironmentService.azureActiveDirectory.clientId);
    __publicField(this, "scopes", []);
    // [`${MsalAuthContext.defaultAudience}/${MsalAuthContext.defaultScope}`];
    __publicField(this, "publicClientApp");
    /**
     * Get the current logged in users tenantId to retrieve the user's account
     * tid may be used in the future as a way for the parent application to set the tid using a query param,
     * as apposed to local storage.
     */
    __publicField(this, "tenantId", new URL(window.location.href).searchParams.get("tid") || /**
    * This may need to be removed
    * If customers are unable to successfully login, they will need to clear local storage
    * I believe this was intended to improve round trips to the login endpoints
    * We do not currently support SPA navigation when redirecting to login
    */
    LocalStorageService.getItem(LAST_TENANT_ID) || EnvironmentService.azureActiveDirectory.tenantId);
    __publicField(this, "expiresOn");
    __publicField(this, "account");
    __publicField(this, "handleRedirectPromise");
    __publicField(this, "isFirstGetTokenRequest");
    __publicField(this, "getTokenNotValidBeforeDates");
    __publicField(this, "navigationClient");
    this.navigationClient = new MsalNavigationClient(history);
    const { publicClientApp, handleRedirectPromise } = this.initPublicClientApp(config) || {};
    this.publicClientApp = publicClientApp;
    this.handleRedirectPromise = handleRedirectPromise;
    this.isFirstGetTokenRequest = true;
    this.getTokenNotValidBeforeDates = /* @__PURE__ */ new Map();
  }
  // TODO: IServicesOptions for ARM endpoint
  get authority() {
    return `${EnvironmentService.azureActiveDirectory.endpoint}/${this.tenantId}`;
  }
  static msalLoggerCallback(logLevel, message, containsPii) {
    if (containsPii) {
      return;
    }
    logger.trackEvent({
      name: TelemetryEventName.Core_MSALAuthContext_Logger,
      properties: {
        msalMessage: message,
        severityLevel: _MsalAuthContext.logLevelToSeverityLevelMap[logLevel],
        type: "Auth"
      },
      measurements: {}
    });
  }
  getTenantId() {
    return this.tenantId;
  }
  isUserLoggedIn() {
    return __async(this, null, function* () {
      yield this.handleRedirectPromise;
      return this.account !== void 0;
    });
  }
  getUser() {
    if (!this.account) {
      const error = new Error("User is not signed in.");
      error.isUserError = true;
      throw error;
    }
    const homeAccountId = this.account.homeAccountId;
    const homeOid = homeAccountId.includes(".") ? homeAccountId.split(".")[0] : homeAccountId;
    return {
      email: this.account.username,
      name: this.account.name || this.account.username,
      tid: this.tenantId,
      homeOid,
      oid: this.account.localAccountId
    };
  }
  getTokenExpiration() {
    return this.expiresOn ? this.expiresOn.getTime() : void 0;
  }
  logIn() {
    return __async(this, null, function* () {
      yield this.handleRedirectPromise;
      const correlationId = v4();
      logger.trackEvent({
        name: TelemetryEventName.Core_MSALAuthContext_login,
        properties: { correlationId },
        measurements: {}
      });
      return this.publicClientApp.loginRedirect({
        scopes: this.scopes,
        authority: this.authority,
        correlationId
      });
    });
  }
  logOut() {
    return __async(this, null, function* () {
      const correlationId = v4();
      logger.trackEvent({
        name: TelemetryEventName.Core_MSALAuthContext_logout,
        properties: { correlationId },
        measurements: {}
      });
      const postLogoutRedirectUri = window.location.href;
      const account = this.getAccount();
      LocalStorageService.removeItem(LAST_TENANT_ID);
      yield this.publicClientApp.logoutRedirect({ account, correlationId, postLogoutRedirectUri });
    });
  }
  logInToTenantSilent(tenantId, setTenantId = true) {
    return __async(this, null, function* () {
      if (setTenantId) {
        this.setTenantId(tenantId);
      }
      const returnUrl = AuthUtils.getOnboardingReturnUrl();
      const { publicClientApp, handleRedirectPromise } = this.initPublicClientApp(null, returnUrl);
      this.publicClientApp = publicClientApp;
      this.handleRedirectPromise = handleRedirectPromise;
      yield this.handleRedirectPromise;
    });
  }
  getToken(tokenParameters, returnExpiry) {
    return __async(this, null, function* () {
      const timingStart = performance.now();
      const correlationId = v4();
      logger.trackEvent({
        name: TelemetryEventName.Core_MSALAuthContext_getToken_Start,
        properties: __spreadProps(__spreadValues({}, tokenParameters), {
          correlationId
        }),
        measurements: {}
      });
      if (!this.account) {
        throw new Error("User is not signed in.");
      }
      const isFirstTokenRequest = this.isFirstGetTokenRequest;
      const authResponse = yield this.getTokenInternal(timingStart, correlationId, tokenParameters);
      if (isFirstTokenRequest) {
        this.isFirstGetTokenRequest = false;
      }
      this.logGetTokenSuccess(tokenParameters, authResponse[0], timingStart, correlationId, isFirstTokenRequest);
      if (returnExpiry) {
        return [authResponse[0].accessToken, authResponse[1]];
      }
      return authResponse[0].accessToken;
    });
  }
  // tslint:disable-next-line: cyclomatic-complexity
  getTokenInternal(timingStart, correlationId, tokenParameters) {
    return __async(this, null, function* () {
      const request = {
        scopes: (tokenParameters == null ? void 0 : tokenParameters.tokenScope) ? [tokenParameters == null ? void 0 : tokenParameters.tokenScope] : this.scopes,
        authority: this.authority,
        redirectUri: this.silentRedirectUri,
        account: this.account,
        correlationId,
        forceRefresh: tokenParameters == null ? void 0 : tokenParameters.forceRefresh
      };
      try {
        const authResponse = yield this.publicClientApp.acquireTokenSilent(request);
        const currentDate = /* @__PURE__ */ new Date();
        const expiresOn = authResponse.expiresOn ? new Date(authResponse.expiresOn) : void 0;
        if (!tokenParameters) {
          this.expiresOn = expiresOn;
          this.account = authResponse.account || void 0;
        }
        const requestKey = JSON.stringify([request.scopes, this.authority]);
        if (authResponse.fromCache) {
          const tokenNotValidBeforeDate = this.getTokenNotValidBeforeDates.get(requestKey);
          if (tokenNotValidBeforeDate && currentDate < tokenNotValidBeforeDate) {
            const parsedTokenExpiry = this.getParsedTokenExpiry(authResponse);
            logger.trackEvent({
              name: TelemetryEventName.Core_MSALAuthContext_getToken_forceRefresh,
              properties: __spreadProps(__spreadValues({}, tokenParameters), {
                tokenExpiry: authResponse.expiresOn ? authResponse.expiresOn.toUTCString() : void 0,
                parsedTokenExpiry,
                duration: Math.round(performance.now() - timingStart),
                tokenSource: "localStorage",
                correlationId,
                tokenNotValidBefore: tokenNotValidBeforeDate == null ? void 0 : tokenNotValidBeforeDate.toUTCString()
              }),
              measurements: {}
            });
            return this.getTokenInternal(timingStart, correlationId, __spreadProps(__spreadValues({}, tokenParameters), {
              tokenScope: _MsalAuthContext.defaultScope,
              forceRefresh: true
            }));
          }
        } else {
          this.getTokenNotValidBeforeDates.set(requestKey, currentDate);
        }
        return [authResponse, expiresOn == null ? void 0 : expiresOn.getTime()];
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError || error instanceof AuthError && error.errorCode === "no_tokens_found") {
          logger.trackEvent({
            name: TelemetryEventName.Core_MSALAuthContext_getToken_Redirect,
            properties: __spreadProps(__spreadValues({}, tokenParameters), {
              correlationId
            }),
            measurements: {}
          });
          this.publicClientApp.acquireTokenRedirect(__spreadProps(__spreadValues({}, request), {
            redirectUri: this.publicClientApp.getConfiguration().auth.redirectUri
          }));
        } else if (error instanceof Error) {
          const duration = Math.round(performance.now() - timingStart);
          this.trackError(error, correlationId, tokenParameters, duration);
          const event = new Event(ErrorCodes.refreshAccessTokenTimeout);
          window.dispatchEvent(event);
        }
        throw error;
      }
    });
  }
  logGetTokenSuccess(tokenParameters, authResponse, timingStart, correlationId, isFirstTokenRequest) {
    const parsedTokenExpiry = this.getParsedTokenExpiry(authResponse);
    logger.trackEvent({
      name: TelemetryEventName.Core_MSALAuthContext_getToken_Success,
      properties: __spreadProps(__spreadValues({}, tokenParameters), {
        tokenExpiry: authResponse.expiresOn ? authResponse.expiresOn.toUTCString() : void 0,
        parsedTokenExpiry,
        duration: Math.round(performance.now() - timingStart),
        tokenSource: authResponse.fromCache ? "localStorage" : "iframe",
        correlationId,
        isFirstTokenRequest
      }),
      measurements: {}
    });
  }
  getParsedTokenExpiry(authResponse) {
    const parsedToken = parseIdToken(authResponse.accessToken);
    let parsedTokenExpiry;
    if (parsedToken == null ? void 0 : parsedToken.exp) {
      parsedTokenExpiry = new Date(parsedToken.exp * 1e3).toUTCString();
    }
    return parsedTokenExpiry;
  }
  trackError(error, correlationId, tokenParameters, duration) {
    let ErrorReason = "UnknownError";
    if (error instanceof BrowserAuthError) {
      ErrorReason = "BrowserAuthError";
    } else if (error instanceof BrowserConfigurationAuthError) {
      ErrorReason = "BrowserConfigurationAuthError";
    } else if (error instanceof InteractionRequiredAuthError) {
      ErrorReason = "InteractionRequiredAuthError";
    }
    const customFields = __spreadValues({
      errorCode: error instanceof AuthError ? error.errorCode : error.name,
      ErrorReason,
      msalMessage: error instanceof AuthError ? error.errorMessage : error.message,
      type: "Auth",
      // @ts-ignore
      duration,
      // @ts-ignore
      correlationId
    }, tokenParameters);
    logger.error(TelemetryExceptionName.Core_MSALAuthContext_trackError, ErrorType.Msal, customFields);
  }
  setTenantId(tenantId) {
    this.tenantId = tenantId;
    const lastTenantId = LocalStorageService.getItem(LAST_TENANT_ID);
    logger.trackEvent({
      name: TelemetryEventName.Core_MSALAuthContext_setTenantId,
      properties: {
        lastTenantId,
        tenantId
      },
      measurements: {}
    });
    LocalStorageService.setItem(LAST_TENANT_ID, tenantId);
    if (lastTenantId !== tenantId) {
      logger.trackEvent({
        name: TelemetryEventName.Core_MSALAuthContext_setTenantId_clearUserAffinity,
        properties: {
          tenantId,
          lastTenantId,
          lastWorkspaceId: LocalStorageService.getItem(LAST_WORKSPACE_ID),
          lastExperimentationGroup: LocalStorageService.getItem(LAST_EXPERIMENTATION_GROUP)
        },
        measurements: {}
      });
      LocalStorageService.removeItem(LAST_WORKSPACE_ID);
      LocalStorageService.removeItem(LAST_EXPERIMENTATION_GROUP);
    }
  }
  getAccount() {
    const accounts = this.publicClientApp.getAllAccounts();
    if (!accounts || !accounts.length) {
      return void 0;
    }
    const foundAccountsForTenant = accounts.filter((account) => account.tenantId === this.tenantId);
    if (foundAccountsForTenant.length > 1) {
      logger.trackEvent({
        name: TelemetryEventName.Core_MSALAuthContext_Multiple_Accounts_For_Tenant,
        properties: {
          AccountsCount: foundAccountsForTenant.length
        },
        measurements: {}
      });
    }
    return foundAccountsForTenant.length ? foundAccountsForTenant[0] : void 0;
  }
  initPublicClientApp(config, returnUrl) {
    logger.trackEvent({
      name: TelemetryEventName.Core_MSALAuthContext_checkBrowserForCookie,
      properties: {},
      measurements: {}
    });
    const browser = Bowser.getParser(window.navigator.userAgent);
    const isIEOrOldEdge = browser.satisfies({ edge: "<79" }) || browser.getBrowserName().toLowerCase() === "internet explorer";
    const defaultConfig = {
      auth: __spreadValues({
        clientId: this.clientId,
        authority: this.authority,
        redirectUri: this.loginRedirectUri,
        postLogoutRedirectUri: this.postLogoutRedirectUri,
        navigateToLoginRequestUrl: true
      }, config == null ? void 0 : config.auth),
      cache: __spreadValues({
        cacheLocation: "localStorage",
        storeAuthStateInCookie: isIEOrOldEdge
      }, config == null ? void 0 : config.cache),
      system: __spreadValues({
        tokenRenewalOffsetSeconds: Constants.tokenRenewalOffsetSeconds,
        iframeHashTimeout: 3e4,
        // iframe is used to refresh the refresh_token as it has a 24h lifetime
        loggerOptions: {
          loggerCallback: _MsalAuthContext.msalLoggerCallback,
          piiLoggingEnabled: false,
          logLevel: LogLevel.Warning
        },
        allowRedirectInIframe: true
      }, config == null ? void 0 : config.system)
    };
    logger.trackEvent({
      name: TelemetryEventName.Core_MSALAuthContext_initMsalClient,
      properties: {},
      measurements: {}
    });
    const publicClientApp = new PublicClientApplication(defaultConfig);
    logger.trackEvent({
      name: TelemetryEventName.Core_MSALAuthContext_handleRedirectPromise,
      properties: {},
      measurements: {}
    });
    const handleRedirectPromise = publicClientApp.handleRedirectPromise().then((response) => {
      if (response == null ? void 0 : response.account) {
        logger.trackEvent({
          name: TelemetryEventName.Core_MSALAuthContext_NewUserLoggedIn,
          properties: {},
          measurements: {}
        });
      } else if (this.getAccount()) {
        logger.trackEvent({
          name: TelemetryEventName.Core_MSALAuthContext_ExistingUserLoggedIn,
          properties: {},
          measurements: {}
        });
      } else {
        logger.trackEvent({
          name: TelemetryEventName.Core_MSALAuthContext_NoUserLoggedIn,
          properties: {},
          measurements: {}
        });
      }
      this.account = (response ? response.account : this.getAccount()) || void 0;
      if (!this.account) {
        const correlationId = v4();
        logger.trackEvent({
          name: TelemetryEventName.Core_MSALAuthContext_login,
          properties: { correlationId },
          measurements: {}
        });
        return this.publicClientApp.loginRedirect({
          scopes: this.scopes,
          authority: this.authority,
          redirectStartPage: returnUrl,
          correlationId
        });
      } else {
        this.setTenantId(this.account.tenantId);
      }
      return void 0;
    }).catch((error) => {
      logger.trackEvent({
        name: TelemetryEventName.Core_MSALAuthContext_redirectError,
        properties: {},
        measurements: {}
      });
      if (error instanceof Error) {
        this.trackError(error);
      }
      this.loginToTenantRedirect(EnvironmentService.azureActiveDirectory.tenantId);
    });
    logger.trackEvent({
      name: TelemetryEventName.Core_MSALAuthContext_initDone,
      properties: {},
      measurements: {}
    });
    return { publicClientApp, handleRedirectPromise };
  }
  catch(error) {
    logger.trackEvent({
      name: TelemetryEventName.Core_MSALAuthContext_initError,
      properties: {},
      measurements: {}
    });
    this.trackError(error);
  }
  loginToTenantRedirect(tenantId) {
    this.setTenantId(tenantId);
    const correlationId = v4();
    logger.trackEvent({
      name: TelemetryEventName.Core_MSALAuthContext_loginToTenantRedirect,
      properties: { correlationId },
      measurements: {}
    });
    this.publicClientApp.loginRedirect({
      scopes: this.scopes,
      authority: this.authority,
      redirectStartPage: AuthUtils.getOnboardingReturnUrl(),
      correlationId
    });
  }
};
__publicField(_MsalAuthContext, "defaultScope", `${EnvironmentService.armResourceEndpoint}/.default`);
__publicField(_MsalAuthContext, "logLevelToSeverityLevelMap", {
  [LogLevel.Verbose]: "Verbose",
  [LogLevel.Info]: "Information",
  [LogLevel.Warning]: "Warning",
  [LogLevel.Error]: "Error"
});
let MsalAuthContext = _MsalAuthContext;
export {
  MsalAuthContext
};
//# sourceMappingURL=MSALAuthContext.js.map
